/**
 * Transport-neutral upload helpers shared by the draft-document create flow
 * (`draft-documents.ts`) and the direct visitor-document manage flow
 * (`visitor-documents.ts`). Plain stateless functions so each flow depends on
 * this module rather than on the other.
 */

/** Abort a stalled upload rather than leaving the promise — and the card — pending forever. */
const UPLOAD_TIMEOUT_MS = 60_000;

/**
 * Bound for one-shot API requests (upload-link save, confirm POST, field-values
 * PATCH). Ember Data's adapter has no transport-level timeout, so a stalled
 * request would otherwise leave the consuming task `.isRunning` forever. The
 * underlying request may still settle later; its result is discarded.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

export async function withRequestDeadline<T>(promise: Promise<T>, label: string, deadlineMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          // Swallow the discarded race-loser's late settlement so it doesn't
          // surface as an unhandled promise rejection.
          promise.catch(() => {});
          reject(new Error(`${label} timed out`));
        }, deadlineMs);
      }),
    ]);
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/**
 * Race a single poll against `deadlineMs`, parameterized over the poll fn so any
 * resource flow (draft-document or visitor-document) can bound its GET. The poll
 * fn is invoked once. Ember Data's adapter has no transport-level timeout, so a
 * hung GET would otherwise let the caller's top-of-loop deadline check be
 * skipped, hanging the poll loop forever.
 */
export function pollWithDeadline<T>(poll: () => Promise<T>, label: string, deadlineMs: number): Promise<T> {
  return withRequestDeadline(poll(), label, deadlineMs);
}

/**
 * PUT the raw file to the presigned upload URL via XHR. Deliberately NOT the
 * Ember Data adapter, ajaxFetch, or `fetch`: the target is object storage, not
 * our API, so the request must carry no auth headers — and XHR is what Mirage
 * (Pretender) intercepts in dev/test (native `fetch` escapes the mock). This
 * mirrors the existing active-storage direct-upload path.
 */
export function uploadFile(uploadLink: string, file: File, options?: { signal?: AbortSignal }): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadLink, true);
    xhr.timeout = UPLOAD_TIMEOUT_MS;

    if (file.type) {
      xhr.setRequestHeader('Content-Type', file.type);
    }

    // Settle-once guard: an abort fired after the PUT already settled must not
    // double-reject (the caller aborts the controller unconditionally on teardown).
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      fn();
    };

    xhr.onload = () =>
      settle(() => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Document upload failed (${xhr.status})`));
        }
      });
    xhr.onerror = () => settle(() => reject(new Error('Document upload failed (network error)')));
    xhr.ontimeout = () => settle(() => reject(new Error('Document upload timed out')));
    xhr.onabort = () => settle(() => reject(new Error('Document upload aborted')));

    // EC cancellation can't reach into a running promise, so the caller's task passes
    // a signal and aborts it on cancel/teardown — otherwise the in-flight presigned
    // PUT would keep uploading (wasted bandwidth, possible orphaned object).
    if (options?.signal) {
      if (options.signal.aborted) {
        settle(() => reject(new Error('Document upload aborted')));
        return;
      }
      options.signal.addEventListener(
        'abort',
        () => {
          if (!settled) xhr.abort();
        },
        { once: true },
      );
    }

    xhr.send(file);
  });
}
